// src/firebase/platformAdmin.js
// Read-only data for the platform admin roster dashboard - org list,
// cohorts, and student rosters/counts across every org. Deliberately never
// touches submissions/markingData/commentBank/settings (no student work).
// Access is gated by firestore.rules' isPlatformAdmin() check, not by
// anything in this file - a non-admin calling these just gets denied.

import { collection, doc, getDoc, getDocs, query, where, setDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

export const getAllOrgs = async () => {
  const snap = await getDocs(collection(db, 'orgs'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllUserProfiles = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Audit trail of support-access sessions (see mintSupportSessionToken /
 * mintReturnToAdminToken in functions/index.js) - who entered which org's
 * dashboard, when, and when they left. Write-only from those two Cloud
 * Functions; this is a read of that log for the platform admin dashboard.
 */
export const getSupportAccessLog = async () => {
  const snap = await getDocs(collection(db, 'supportAccessLog'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.enteredAt?.toMillis?.() || 0) - (a.enteredAt?.toMillis?.() || 0));
};

/**
 * Per-org AI summary usage (call count, tokens, $ cost) - written by
 * generateAiSummaryForSubmission in functions/index.js on every successful
 * call. Doc id is the orgId; an org with no calls yet just has no doc.
 */
export const getAllAiSummaryUsage = async () => {
  const snap = await getDocs(collection(db, 'aiSummaryUsage'));
  return snap.docs.map(d => ({ orgId: d.id, ...d.data() }));
};

/**
 * Billing enrolment log for one org (see onUserEnrolmentChange in
 * functions/index.js) - which students joined and left which cohorts, and
 * when. Read by the billing report.
 */
export const getBillingEnrolments = async (orgId) => {
  const snap = await getDocs(query(collection(db, 'billingEnrolments'), where('orgId', '==', orgId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * One org's AI summary usage per calendar month - [{ month: 'YYYY-MM',
 * calls, costUsd }], newest first. Only months since this was added.
 */
export const getAiUsageByMonth = async (orgId) => {
  const snap = await getDocs(collection(db, 'aiSummaryUsage', orgId, 'months'));
  return snap.docs
    .map(d => ({ month: d.id, ...d.data() }))
    .sort((a, b) => b.month.localeCompare(a.month));
};

/**
 * Students the platform admin has flagged as test accounts (testAccounts/{uid})
 * - left out of billing and student counts. Returns a Set of uids.
 */
export const getTestAccountIds = async () => {
  const snap = await getDocs(collection(db, 'testAccounts'));
  return new Set(snap.docs.map(d => d.id));
};

export const setTestAccount = async (uid, isTest, name = '') => {
  const ref = doc(db, 'testAccounts', uid);
  if (isTest) {
    await setDoc(ref, { name, markedAt: serverTimestamp() });
  } else {
    await deleteDoc(ref);
  }
};

/**
 * Invoices recorded from the billing report (billingInvoices) - which
 * enrolments went on which invoice, so no student is charged twice for the
 * same cohort. Platform admin only.
 */
export const getBillingInvoices = async (orgId) => {
  const snap = await getDocs(query(collection(db, 'billingInvoices'), where('orgId', '==', orgId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createBillingInvoice = async (invoice) => {
  await addDoc(collection(db, 'billingInvoices'), { ...invoice, invoicedAt: serverTimestamp() });
};

export const deleteBillingInvoice = async (invoiceId) => {
  await deleteDoc(doc(db, 'billingInvoices', invoiceId));
};

/**
 * Every org's cohorts in Recently Deleted (pendingDeletion), for the
 * Platform Admin screen
 */
export const getAllRecentlyDeletedCohorts = async () => {
  const snap = await getDocs(query(collection(db, 'cohorts'), where('pendingDeletion', '==', true)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Delete ('delete' - into the tutor's Recently Deleted), restore, or
 * permanently delete ('deleteNow') any tutor's cohort - see
 * adminCohortAction in functions/index.js. Logged in adminActionLog.
 */
export const adminCohortAction = async (cohortId, action) => {
  const result = await httpsCallable(getFunctions(getApp()), 'adminCohortAction')({ cohortId, action });
  return result.data;
};

// ---- Monthly invoicing (see invoiceAction in functions/index.js) ----

export const invoiceAction = async (data) => {
  const result = await httpsCallable(getFunctions(getApp()), 'invoiceAction', { timeout: 300000 })(data);
  return result.data;
};

/** FreeAgent link: status, connect, disconnect, listContacts, linkContact, createContact, sync */
export const freeagentAction = async (data) => {
  const result = await httpsCallable(getFunctions(getApp()), 'freeagentAction', { timeout: 300000 })(data);
  return result.data;
};

/** The platform admin's business and bank details for invoices */
export const getInvoiceSettings = async () => {
  const snap = await getDoc(doc(db, 'platformSettings', 'invoicing'));
  return snap.exists() ? snap.data() : {};
};

/** Saves the editable settings - nextNumber is only changed by the server, unless set here deliberately */
export const saveInvoiceSettings = async (settings) => {
  await setDoc(doc(db, 'platformSettings', 'invoicing'), settings, { merge: true });
};

/** Each tutor's billing contact and price - { [orgId]: { billingEmail, billingName, billingAddress, pricePerStudent } } */
export const getAllOrgBilling = async () => {
  const snap = await getDocs(collection(db, 'orgBilling'));
  return Object.fromEntries(snap.docs.map(d => [d.id, d.data()]));
};

export const saveOrgBilling = async (orgId, billing) => {
  await setDoc(doc(db, 'orgBilling', orgId), billing, { merge: true });
};

/** Every invoice sent from the platform (not manual "Mark as invoiced" records), newest first */
export const getSentInvoices = async () => {
  const snap = await getDocs(query(collection(db, 'billingInvoices'), where('kind', '==', 'invoice')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.invoicedAtIso || '').localeCompare(a.invoicedAtIso || ''));
};

/** Additional charges (e.g. set-up work) not yet invoiced, newest first */
export const getPendingExtras = async () => {
  const [extras, invoices] = await Promise.all([
    getDocs(collection(db, 'billingExtras')),
    getDocs(query(collection(db, 'billingInvoices'), where('kind', '==', 'invoice')))
  ]);
  const invoiced = new Set(invoices.docs.flatMap(d => d.data().extraIds || []));
  return extras.docs.map(d => ({ id: d.id, ...d.data() }))
    .filter(x => !invoiced.has(x.id))
    .sort((a, b) => (b.addedAt?.toMillis?.() || 0) - (a.addedAt?.toMillis?.() || 0));
};

export const addExtraCharge = async ({ orgId, description, hours, rate }) => {
  await addDoc(collection(db, 'billingExtras'), { orgId, description, hours: Number(hours), rate: Number(rate), addedAt: serverTimestamp() });
};

/** Only for a charge that hasn't been invoiced yet */
export const removeExtraCharge = async (chargeId) => {
  await deleteDoc(doc(db, 'billingExtras', chargeId));
};
